import React, { PropsWithChildren } from 'react';
import { ListItem } from 'app/area/shared/list-components/list-item';
import { ListItemBody } from 'app/area/shared/list-components/list-item-body';
import { ListItemHeader } from 'app/area/shared/list-components/list-item-header';
import { UpdateExternalUrlItemHandler } from '../types';
import { ExternalUrlDrag } from './external-url-drag';
import { ExternalUrlDrop } from './external-url-drop';
import { ExternalUrlMeatball } from './external-url-meatball';
import { ExternalUrlTileHeader } from './external-url-tile-header';
import { ExternalUrlDragItemType, ExternalUrlFormIds } from 'app/area/external-url-groups/form/constants';
import { ExternalUrlItem, ExternalUrlsValidation, ExternalUrlValidation } from 'app/area/external-url-groups/form/types-store';
import { ExternalUrlGroupsTranslation } from 'app/area/external-url-groups/constants';
import { ExternalUrlText } from 'app/area/external-url-groups/form/components/external-url-text';
import { ExternalUrlManageUrl } from 'app/area/external-url-groups/form/components/external-url-manage-url';
import styles from './external-url-tile.scss';

interface ExternalUrlTileProps {
  externalUrlItem: ExternalUrlItem;
  prevElementSequence: number;
  nextElementSequence: number;
  externalUrlsValidation: ExternalUrlsValidation;
  translation: ExternalUrlGroupsTranslation;
  onAddExternalUrl: (sequence?: number) => void;
  updateValidation: (validation: ExternalUrlValidation) => void;
  displayError: (errorMessage: string) => void;
  updateExternalUrl: UpdateExternalUrlItemHandler;
  deleteExternalUrl: (externalUrlId: number) => void;
  deleteAllExternalUrls: () => void;
}

export const ExternalUrlTile = ({
  externalUrlItem, prevElementSequence, nextElementSequence,
  externalUrlsValidation, translation, onAddExternalUrl, updateExternalUrl,
  deleteExternalUrl, deleteAllExternalUrls
}: PropsWithChildren<ExternalUrlTileProps>) => {

  const onDrag = (dragExternalUrl: ExternalUrlItem, sequence: number) => {
    updateExternalUrl(
      dragExternalUrl?.code?.value,
      { ...dragExternalUrl, sequence },
      externalUrlsValidation[dragExternalUrl?.code?.value]
    );
  };

  const onHideDetails = () => {
    deleteExternalUrl(undefined);
  };

  const onUrlTemplateChange = (urlTemplate: string) => {
    updateExternalUrl(externalUrlItem?.code?.value, { ...externalUrlItem, urlTemplate }, undefined);
  };

  const externalUrlValidation = externalUrlsValidation
    ?.[externalUrlItem.code.value]
    ?.code?.messages?.find(message => message?.parameters?.code === externalUrlItem?.code?.value);

  return <div className="col-12">
    <ExternalUrlDrop
      item={externalUrlItem}
      type={ExternalUrlDragItemType.externalUrlTile}
      updateItem={onDrag}>
      <div className={`${styles.tileWrapper} ${!!externalUrlValidation ? styles.hasError : ''}`}>
        <ListItem>
          <ExternalUrlDrag
            item={externalUrlItem}
            type={ExternalUrlDragItemType.externalUrlTile}
            canDrag={true}>
            <ListItemHeader>
              <ExternalUrlTileHeader
                externalUrlItem={externalUrlItem}
                canDrag={true}
                disabledLabel={translation.disabled} />
              <ExternalUrlManageUrl
                externalUrlItem={externalUrlItem}
                translation={translation} />
              <ExternalUrlMeatball
                externalUrlItem={externalUrlItem}
                prevElementSequence={prevElementSequence}
                nextElementSequence={nextElementSequence}
                onAddExternalUrl={onAddExternalUrl}
                updateExternalUrl={updateExternalUrl}
                deleteExternalUrl={deleteExternalUrl}
                deleteAllExternalUrls={deleteAllExternalUrls}
                onHideDetails={onHideDetails}
                validation={externalUrlsValidation}
                translation={translation} />
            </ListItemHeader>
          </ExternalUrlDrag>
          <ListItemBody>
            <div className={styles.bodyWrapper}>
              <div className="w-100">
                <div className="row">
                  <div className="col-12">
                    <ExternalUrlText
                      id={ExternalUrlFormIds.urlTemplate}
                      value={externalUrlItem?.urlTemplate}
                      onChange={onUrlTemplateChange}
                      labelText={translation.external_url_template}
                      validation={externalUrlsValidation
                        ?.[externalUrlItem.code.value]
                        ?.code} />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    {!!externalUrlItem?.code?.longDescription
                      ? <ExternalUrlText
                        id={ExternalUrlFormIds.longDescription}
                        value={externalUrlItem?.code?.longDescription}
                        labelText={translation.external_url_long_description} />
                      : <p className="no-content">
                        {translation.external_url_long_description_info}
                      </p>}
                  </div>
                </div>
              </div>
            </div>
          </ListItemBody>
        </ListItem>
      </div>
    </ExternalUrlDrop>
  </div>;
};
